/* File : src/contexts/DataContext.js */
import React, { createContext, useState, useContext } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// Création du contexte de données
const DataContext = createContext(null);

// Hook personnalisé pour utiliser le contexte de données
export const useData = () => useContext(DataContext);

// Fournisseur du contexte de données
export const DataProvider = ({ children }) => {
  // États pour les différentes données
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [entries, setEntries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Obtenir le contexte d'authentification
  const { user } = useAuth() || { user: null };

  // Fonction pour récupérer les entrées récentes
  const fetchRecentEntries = async (limit = 10) => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/scanner/recent-entries', { params: { limit } });
      
      if (response.data && response.data.entries) {
        setEntries(response.data.entries);
        return response.data.entries;
      } else {
        throw new Error('Format de réponse inattendu');
      }
    } catch (err) {
      console.error('Error fetching recent entries:', err);
      setError('Erreur lors de la récupération des entrées récentes: ' + 
              (err.response?.data?.message || err.message || 'Veuillez réessayer.'));
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour scanner un ticket
  const scanTicket = async (qrData) => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Vérifier la connexion Internet
      const isOnline = navigator.onLine;
      
      if (isOnline) {
        const response = await api.post('/scan', { qrData });
        
        if (response.data) {
          // Ajouter l'entrée à la liste
          setEntries(prevEntries => [response.data, ...prevEntries]);
          return response.data;
        } else {
          throw new Error('Format de réponse inattendu');
        }
      } else {
        // Hors ligne : stocker localement
        const offlineEntry = {
          qrData,
          scanTime: new Date().toISOString(),
          isOffline: true,
          deviceId: user?.username || 'unknown'
        };
        
        // Sauvegarder dans localStorage
        const storedScans = localStorage.getItem('offlineScans');
        const offlineScans = storedScans ? JSON.parse(storedScans) : [];
        offlineScans.push(offlineEntry);
        localStorage.setItem('offlineScans', JSON.stringify(offlineScans));
        
        // Ajouter à la liste locale
        setEntries(prevEntries => [offlineEntry, ...prevEntries]);
        
        return offlineEntry;
      }
    } catch (err) {
      console.error('Error scanning ticket:', err);
      setError('Erreur lors du scan du ticket: ' + 
               (err.response?.data?.message || err.message || 'Veuillez réessayer.'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour synchroniser les scans hors ligne
  const syncOfflineScans = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Récupérer les scans hors ligne depuis localStorage
      const storedScans = localStorage.getItem('offlineScans');
      const offlineScans = storedScans ? JSON.parse(storedScans) : [];
      
      if (offlineScans.length === 0) {
        return { success: true, message: "Aucun scan hors ligne à synchroniser" };
      }
      
      // Synchroniser avec le serveur
      const response = await api.post('/sync-offline-scans', { scans: offlineScans });
      
      if (response.data && response.data.success) {
        // Supprimer les scans synchronisés
        localStorage.removeItem('offlineScans');
        
        // Mise à jour de l'interface
        fetchRecentEntries();
        
        return response.data;
      } else {
        throw new Error('Échec de la synchronisation');
      }
    } catch (err) {
      console.error('Error syncing offline scans:', err);
      setError('Erreur lors de la synchronisation des scans hors ligne: ' + 
               (err.response?.data?.message || err.message || 'Veuillez réessayer.'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Valeur à fournir au contexte
  const value = {
    users,
    tickets,
    entries,
    statistics,
    loading,
    error,
    totalCount,
    scanTicket,
    syncOfflineScans,
    fetchRecentEntries
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;